import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Download, RotateCcw, TrendingDown, TrendingUp, Minus, Phone, Mail, ExternalLink, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';
import AppShell from '@/components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RISK_STYLES = {
  green: { hex: '#10B981', bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'border-emerald-500', label: 'Low Risk' },
  amber: { hex: '#F59E0B', bg: 'bg-amber-50', text: 'text-amber-700', ring: 'border-amber-500', label: 'Moderate Risk' },
  red: { hex: '#EF4444', bg: 'bg-red-50', text: 'text-red-700', ring: 'border-red-500', label: 'High Risk' }
};

const ResultsScreen = ({ assessment }) => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (!assessment) navigate('/');
  }, [assessment, navigate]);

  if (!assessment) return null;

  const risk = RISK_STYLES[assessment.risk_color] || RISK_STYLES.amber;
  const scoreValue = parseFloat(assessment.risk_index.toFixed(1));
  const industryAvg = parseFloat(assessment.industry_comparison.industry_avg.toFixed(1));

  const gaugeData = [{ name: 'score', value: scoreValue, fill: risk.hex }];

  const chartData = [
    { name: 'Your Business', value: scoreValue, fill: risk.hex },
    { name: 'Industry Avg.', value: industryAvg, fill: '#004B87' }
  ];

  const getRiskIcon = () => {
    if (assessment.risk_color === 'green') return <TrendingDown className="w-5 h-5" strokeWidth={1.75} />;
    if (assessment.risk_color === 'amber') return <Minus className="w-5 h-5" strokeWidth={1.75} />;
    return <TrendingUp className="w-5 h-5" strokeWidth={1.75} />;
  };

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      const response = await axios.get(`${API}/assessment/${assessment.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EYB_Resilience_Report_${assessment.business_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {/* Header row */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-2">
              Assessment Report
            </div>
            <h1 className="font-display text-3xl lg:text-[40px] font-light leading-tight text-[#0A192F]">
              {assessment.business_name}
            </h1>
            <div className="text-slate-500 text-sm mt-1 capitalize">{assessment.industry} &middot; Business Resilience Index</div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              variant="outline"
              className="rounded-sm border-slate-300 hover:border-[#004B87] hover:text-[#004B87] text-[13px] uppercase tracking-[0.18em] font-semibold h-11 px-5"
              data-testid="download-pdf-button"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" strokeWidth={1.75} />
                  Export PDF
                </>
              )}
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="rounded-sm hover:bg-slate-100 text-[13px] uppercase tracking-[0.18em] font-semibold h-11 px-5"
              data-testid="new-assessment-button"
            >
              <RotateCcw className="w-4 h-4 mr-2" strokeWidth={1.75} />
              New
            </Button>
          </div>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Hero score */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="lg:col-span-8 bg-white border border-slate-200 p-8 lg:p-10"
            data-testid="hero-score-card"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-3">
                  Overall Risk Index
                </div>
                <div className="flex items-baseline gap-2 mb-4">
                  <span
                    className="font-display text-7xl lg:text-8xl font-light leading-none"
                    style={{ color: risk.hex }}
                    data-testid="risk-index"
                  >
                    {scoreValue}
                  </span>
                  <span className="text-3xl text-slate-400 font-light">%</span>
                </div>
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border ${risk.ring} ${risk.bg} ${risk.text} text-[12px] uppercase tracking-[0.18em] font-semibold`}
                  data-testid="risk-level"
                >
                  {getRiskIcon()}
                  {assessment.risk_level}
                </div>
                <p className="text-sm text-slate-600 mt-6 max-w-md leading-relaxed">
                  Your resilience index is calculated from your responses across competitive rivalry,
                  supplier &amp; customer bargaining power, threat of substitutes and new entrants.
                </p>
              </div>

              {/* Gauge */}
              <div className="w-56 h-56 relative flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart
                    cx="50%"
                    cy="50%"
                    innerRadius="72%"
                    outerRadius="100%"
                    barSize={16}
                    data={gaugeData}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                    <RadialBar background={{ fill: '#F1F5F9' }} dataKey="value" cornerRadius={0} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-400">Score</div>
                  <div className="font-display text-3xl font-light" style={{ color: risk.hex }}>
                    {scoreValue}%
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Industry comparison chart */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-4 bg-white border border-slate-200 p-8"
            data-testid="industry-chart-card"
          >
            <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-2">
              Benchmark
            </div>
            <h3 className="font-display text-xl font-medium text-[#0A192F] mb-6">
              Vs. Industry Average
            </h3>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 24, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={100}
                    tick={{ fontSize: 12, fill: '#334155' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: '#F8FAFC' }}
                    contentStyle={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 2, fontSize: 12 }}
                    formatter={(value) => [`${value}%`, 'Risk Index']}
                  />
                  <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                    {chartData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Industry comparison narrative */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="lg:col-span-12 bg-white border border-slate-200 p-8 lg:p-10"
            data-testid="industry-comparison-card"
          >
            <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-3">
              Industry Analysis
            </div>
            <h3 className="font-display text-xl lg:text-2xl font-medium text-[#0A192F] mb-4">
              How your business compares
            </h3>
            <p className="text-[15px] text-slate-700 leading-[1.7] max-w-4xl" data-testid="industry-comparison">
              {assessment.industry_comparison.comparison_text}
            </p>
          </motion.div>

          {/* Recommendations CTA */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-12 bg-[#0A192F] text-white p-8 lg:p-10 relative overflow-hidden"
            data-testid="cta-card"
          >
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)',
                backgroundSize: '40px 40px'
              }}
            />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[#7DD3FC] mb-3">
                  Strategic Recommendations
                </div>
                <h3 className="font-display text-2xl lg:text-3xl font-light leading-tight mb-4">
                  Ready to turn insights into action?
                </h3>
                <p className="text-slate-300 text-[15px] leading-relaxed" data-testid="contact-info">
                  For tailored strategic recommendations to strengthen your business
                  resilience, book an appointment with our EYB Consulting team.
                </p>
              </div>
              <div className="lg:col-span-5 space-y-4">
                <a
                  href="https://eybconsulting.com.au/get-started/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 bg-white text-[#0A192F] px-5 py-4 rounded-sm hover:bg-slate-100 transition-colors group"
                  data-testid="book-consultation-cta"
                >
                  <span className="text-[13px] uppercase tracking-[0.18em] font-semibold">
                    Book a Consultation
                  </span>
                  <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                </a>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <a
                    href="tel:0422731122"
                    className="flex items-center gap-3 border border-white/20 px-4 py-3 rounded-sm hover:bg-white/5 transition-colors"
                    data-testid="contact-phone"
                  >
                    <Phone className="w-4 h-4 text-[#7DD3FC] flex-shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Call</div>
                      <div className="text-sm text-white">0422 731 122</div>
                    </div>
                  </a>
                  <a
                    href="mailto:info@eybconsulting.com.au"
                    className="flex items-center gap-3 border border-white/20 px-4 py-3 rounded-sm hover:bg-white/5 transition-colors"
                    data-testid="contact-email"
                  >
                    <Mail className="w-4 h-4 text-[#7DD3FC] flex-shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Email</div>
                      <div className="text-[13px] text-white truncate">info@eybconsulting.com.au</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AppShell>
  );
};

export default ResultsScreen;
